const CHAR_UPPER_A = 0x41;
const CHAR_LOWER_A = 0x61;
const CHAR_UPPER_Z = 0x5a;
const CHAR_LOWER_Z = 0x7a;
const CHAR_0 = 0x30;
const CHAR_9 = 0x39;
const CHAR_SPACE = 0x20;
const CHAR_UNDERSCORE = 0x5f;

type ObjectOrArray = Array<unknown> | Record<string, unknown>;

function camelize(str: string) {
  const firstChar = str.charCodeAt(0);

  if (isDigit(firstChar) || isNaN(firstChar)) {
    return str;
  }

  let changed = isUpper(firstChar);
  const transformed = changed ? [toLower(firstChar)] : [firstChar];

  const length = str.length;
  for (let i = 1; i < length; i++) {
    let c = str.charCodeAt(i);

    if (c === CHAR_UNDERSCORE || c === CHAR_SPACE) {
      changed = true;
      c = str.charCodeAt(++i);

      if (isNaN(c)) {
        return str;
      }

      if (isLower(c)) {
        transformed.push(toUpper(c));
      } else {
        transformed.push(c);
      }
    } else {
      transformed.push(c);
    }
  }

  if (!changed) {
    return str;
  }

  return String.fromCharCode.apply(undefined, transformed);
}

export function camelizeKeys(obj: ObjectOrArray) {
  if (!isObjectOrArray(obj)) {
    return obj;
  }

  return transformKeys(obj, camelize);
}

function isDigit(c: number) {
  return CHAR_0 <= c && c <= CHAR_9;
}

function isLower(c: number) {
  return CHAR_LOWER_A <= c && c <= CHAR_LOWER_Z;
}

function isObjectOrArray(value: unknown): value is ObjectOrArray {
  return (
    Boolean(value) &&
    typeof value === 'object' &&
    !(value instanceof Function) &&
    !(value instanceof Date)
  );
}

function isUpper(c: number) {
  return CHAR_UPPER_A <= c && c <= CHAR_UPPER_Z;
}

function toLower(c: number) {
  return c + 0x20;
}

function toUpper(c: number) {
  return c - 0x20;
}

function transformArray(
  array: Array<unknown>,
  transform: (key: string, sep?: string) => string,
  sep?: string,
) {
  const length = array.length;
  const transformed = Array.from({ length });
  let idx = 0;
  for (const item of array) {
    transformed[idx++] = isObjectOrArray(item)
      ? transformKeys(item, transform, sep)
      : item;
  }
  return transformed;
}

function transformKeys(
  obj: Array<unknown> | ObjectOrArray,
  transform: (key: string, sep?: string) => string,
  sep?: string,
) {
  if (Array.isArray(obj)) {
    return transformArray(obj, transform, sep);
  }

  if (typeof obj.prototype !== 'undefined') {
    return obj;
  }

  const transformed: Record<string, unknown> = {};
  for (const key in obj) {
    const value = obj[key];
    const nextKey = transform(key, sep);
    transformed[nextKey] = isObjectOrArray(value)
      ? transformKeys(value, transform, sep)
      : value;
  }
  return transformed;
}
